// src/pages/draft_document/ResolutionList.jsx
// ✅ 결의 목록 — DraftDocumentList.jsx 구조를 기반으로 결의(expense_resolution/
// income_resolution) 전용으로 특화. filterset_fields에 "IN" 조회가 없어 두
// document_type을 병렬로 각각 조회한 뒤 병합·정렬한다(백엔드 변경 없음 — 목록
// API 응답에 이미 template_data가 포함돼 있어 내부기안번호/지출일·입금일을
// 재계산 없이 그대로 읽을 수 있음, 사전 확인 완료).
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./ResolutionList.module.css";
import api from "../../api";
import {
  CATEGORY_LABEL,
  internalDraftNoOf,
  transactionDateOf,
} from "../../utils/resolutionDraft";

const DOC_TYPE_LABEL = {
  expense_resolution: "지출결의",
  income_resolution: "수입결의",
};

const DOC_TYPE_BADGE_CLASS = {
  expense_resolution: "docTypeExpense",
  income_resolution: "docTypeIncome",
};

export default function ResolutionList() {
  useEffect(() => {
    document.title = "결의 목록";
  }, []);

  const navigate = useNavigate();

  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    kind: "",          // "" | "expense_resolution" | "income_resolution" — 클라이언트 필터
    category: "",
    search: "",
    start_date: "",
    end_date: "",
  });

  const fetchResolutions = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.category) params.append("category", filters.category);
      if (filters.search) params.append("search", filters.search);
      if (filters.start_date) params.append("start_date", filters.start_date);
      if (filters.end_date) params.append("end_date", filters.end_date);
      const qs = params.toString();

      // ✅ document_type은 exact-match 필터만 지원(IN 조회 불가)이라 지출/수입
      // 결의를 각각 조회 후 병합한다.
      const [expenseRes, incomeRes] = await Promise.all([
        api.get(`/draft/documents/?document_type=expense_resolution&${qs}`),
        api.get(`/draft/documents/?document_type=income_resolution&${qs}`),
      ]);
      const expenseList = Array.isArray(expenseRes.data) ? expenseRes.data : expenseRes.data.results || [];
      const incomeList = Array.isArray(incomeRes.data) ? incomeRes.data : incomeRes.data.results || [];

      // ✅ 정렬 기본 작성일 역순(동률이면 최근 생성분 우선)
      const merged = [...expenseList, ...incomeList].sort((a, b) => {
        const dateDiff = (b.draft_date || "").localeCompare(a.draft_date || "");
        if (dateDiff !== 0) return dateDiff;
        return (b.created_at || "").localeCompare(a.created_at || "");
      });

      setDocuments(merged);
    } catch (err) {
      console.error("❌ 결의 목록 불러오기 실패:", err);
      alert("결의 목록을 불러오는데 실패했습니다.");
    } finally {
      setLoading(false);
    }
  }, [filters.category, filters.search, filters.start_date, filters.end_date]);

  useEffect(() => {
    fetchResolutions();
  }, [fetchResolutions]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleViewDetail = (id) => {
    navigate(`/approval/draftdocument/${id}`);
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft:    { text: "작성중", cls: styles.badgeDraft },
      pending:  { text: "결재중", cls: styles.badgePending },
      approved: { text: "승인",   cls: styles.badgeApproved },
      rejected: { text: "반려",   cls: styles.badgeRejected },
    };
    return badges[status] || { text: status, cls: styles.badgeDraft };
  };

  // ✅ 구분(전체/지출/수입) 필터는 서버 재조회 없이 이미 받아온 병합 목록에서 거른다
  const visibleDocuments = filters.kind
    ? documents.filter((doc) => doc.document_type === filters.kind)
    : documents;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>📋 결의 목록</h2>
      </div>

      {/* 필터 영역 */}
      <div className={styles.filters}>
        <div className={styles.filterRow}>
          <select
            value={filters.kind}
            onChange={(e) => handleFilterChange("kind", e.target.value)}
            className={styles.filterSelect}
          >
            <option value="">전체 구분</option>
            <option value="expense_resolution">지출결의</option>
            <option value="income_resolution">수입결의</option>
          </select>

          <select
            value={filters.category}
            onChange={(e) => handleFilterChange("category", e.target.value)}
            className={styles.filterSelect}
          >
            <option value="">전체 사업구분</option>
            {Object.keys(CATEGORY_LABEL).map((key) => (
              <option key={key} value={key}>{CATEGORY_LABEL[key]}</option>
            ))}
          </select>

          <input
            type="date"
            value={filters.start_date}
            onChange={(e) => handleFilterChange("start_date", e.target.value)}
            className={styles.filterDate}
          />
          <input
            type="date"
            value={filters.end_date}
            onChange={(e) => handleFilterChange("end_date", e.target.value)}
            className={styles.filterDate}
          />

          <input
            type="text"
            value={filters.search}
            onChange={(e) => handleFilterChange("search", e.target.value)}
            className={styles.filterSearch}
            placeholder="제목, 내부기안번호 검색..."
          />
        </div>
      </div>

      {/* 테이블 */}
      <div className={styles.tableWrapper}>
        {loading ? (
          <div className={styles.loading}>불러오는 중...</div>
        ) : visibleDocuments.length === 0 ? (
          <div className={styles.empty}>결의서가 없습니다.</div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>구분</th>
                <th>문서제목</th>
                <th>내부기안번호</th>
                <th>사업구분</th>
                <th>금액</th>
                <th>지출일·입금일</th>
                <th>작성자</th>
                <th>작성일</th>
                <th>결재상태</th>
              </tr>
            </thead>
            <tbody>
              {visibleDocuments.map((doc) => {
                const statusBadge = getStatusBadge(doc.status);
                return (
                  <tr
                    key={doc.id}
                    className={styles.clickableRow}
                    onClick={() => handleViewDetail(doc.id)}
                  >
                    <td>
                      <span className={`${styles.docTypeTag} ${styles[DOC_TYPE_BADGE_CLASS[doc.document_type]] || ""}`}>
                        {DOC_TYPE_LABEL[doc.document_type] || doc.document_type}
                      </span>
                    </td>
                    <td className={styles.title}>{doc.title}</td>
                    <td className={styles.draftNo}>{internalDraftNoOf(doc)}</td>
                    <td>{CATEGORY_LABEL[doc.category] || doc.category}</td>
                    <td className={styles.amount}>{Number(doc.amount).toLocaleString()}원</td>
                    <td>{transactionDateOf(doc) || "-"}</td>
                    <td>{doc.created_by_name}</td>
                    <td>{doc.draft_date}</td>
                    <td>
                      <span className={`${styles.badge} ${statusBadge.cls}`}>
                        {statusBadge.text}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
